import { useEffect, useState } from 'react'
import { fetchAuthedBlobURL } from '../api/client'

// Chat attachments can't be loaded straight into an <img src> because the
// serving endpoint needs device auth (no way to set headers on an <img>), so we
// fetch the bytes with auth and render the resulting object URL.
//
// Module-level cache keyed by the attachment url: the same image shown in
// several places (or across re-renders) fetches exactly once. Attachments are
// immutable, so a cached blob never goes stale; object URLs are intentionally
// kept for the session rather than revoked, trading a little memory for no
// re-fetch flicker and no revoke races between components sharing an image.
const cache = new Map<string, Promise<string>>()

function load(url: string): Promise<string> {
  let p = cache.get(url)
  if (!p) {
    p = fetchAuthedBlobURL(url, { kind: 'chat_attachment', action: 'download' }).catch((e) => {
      cache.delete(url) // let a later mount retry instead of caching the failure
      throw e
    })
    cache.set(url, p)
  }
  return p
}

// useAttachmentBlob loads an authenticated attachment URL into an object URL.
// Returns { src } once ready, or { error: true } if the fetch failed.
export function useAttachmentBlob(url: string | undefined): { src?: string; error: boolean } {
  const [src, setSrc] = useState<string | undefined>(undefined)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!url) {
      setSrc(undefined)
      return
    }
    let cancelled = false
    setError(false)
    load(url)
      .then((s) => {
        if (!cancelled) setSrc(s)
      })
      .catch(() => {
        if (!cancelled) setError(true)
      })
    return () => {
      cancelled = true
    }
  }, [url])

  return { src, error }
}
